import { CurrencyView } from '../currency-view';
import { BlockLoading } from '../fetch-status/block-loading';
import { EndStatus } from '../fetch-status/end-status';
import { PositionNothing } from '../fetch-status/position-nothing';
import { EventOutcomeName, EventPrimaryTitle } from '../market-event-title';
import { OptionsPositionBadge } from '../options-positions-badge';
import { PendingPositionRow } from '../pending-trade/pending-trade-rows';
import { TextTooltip } from '../text-tooltip';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { UploadedImage } from '../uploaded-image';
import { SharePosition } from './share-position';
import { IS_DEBUG } from '@/client/confs/app';
import { useAppSettingStore } from '@/client/store/app-store';
import { useModelStore } from '@/client/store/model-store';
import { usePendingTradesFor } from '@/client/store/pending-trades-store';
import { useTradeParamsStore } from '@/client/store/trade-store';
import { cn } from '@/client/utils';
import { api } from '@/client/utils/api';
import { eventPrimaryTitleText } from '@/client/utils/i18n-content';
import { useEventLabels } from '@/client/hooks/use-event-labels';
import useLocale from '@/client/hooks/use-locale';
import { useUserPositions } from '@/libs/react-sdk/api/use-user-options';
import { ClaimStatus, PositionStatus, UserPositionInfo } from '@/libs/sdk/api';
import useInViewport from 'ahooks/lib/useInViewport';
import { BasicTarget } from 'ahooks/lib/utils/domTarget';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';

// Positions
export const PositionsItemDT = ({
  label,
  value,
  decimal = 6,
  isChange = false,
  baseTokenAddress,
}: {
  label: string;
  value: any;
  decimal?: number;
  isChange?: boolean;
  // The position's own collateral, so the coin icon reflects the position's token
  // instead of the global lens (avoids showing AIP on a USDT position after switch).
  baseTokenAddress?: string;
}) => {
  return (
    <div className="flex flex-col">
      <div className="text-3 leading-[normal] font-medium text-foreground/40">{label}</div>
      <CurrencyView
        isChange={isChange}
        value={value}
        decimal={decimal}
        precision={2}
        className="text-sm leading-[normal] font-medium"
      />
    </div>
  );
};

type PositionsItemProps = {
  className?: string;
  root?: BasicTarget<Element>;
  onViewChange?: (isInView: boolean) => void;
  uid: string;
  position: UserPositionInfo;
};

export const PositionsItem = ({ root, onViewChange, uid, position, className }: PositionsItemProps) => {
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const ref = useRef<HTMLDivElement>(null);
  useInViewport(ref, {
    root,
    callback: entry => onViewChange?.(entry.isIntersecting),
  });
  const setSellOpen = useModelStore(state => state.setSellOpen);
  const setSellParams = useModelStore(state => state.setSellParams);
  const setTradeOpen = useModelStore(state => state.setTradeOpen);
  const setTradeParams = useTradeParamsStore(state => state.setTradeParams);
  const labels = useEventLabels([position.marketAddress]);
  const label = labels[position.marketAddress.toLowerCase()];
  const { locale } = useLocale();
  return (
    <div
      ref={ref}
      onClick={() => {
        navigate(`/market/${position.marketAddress}`);
      }}
      className={cn(
        'min-h-[137px] py-3 ps-4 pe-3 border border-foreground/10 rounded-4 hover:bg-foreground/5 cursor-pointer bg-card',
        className
      )}
    >
      <div className="flex items-center gap-3">
        <UploadedImage fileKey={position.marketPicUrl} className="size-9 rounded-2" />
        <div className="space-y-1 flex-1">
          <TextTooltip
            tooltip={eventPrimaryTitleText(label, position.marketName, locale)}
            className="text-sm line-clamp-1 text-start leading-4.25 font-medium text-foreground market-title-hover"
          >
            <EventPrimaryTitle label={label} marketName={position.marketName} />
          </TextTooltip>
          <div className="flex items-center gap-1.5">
            <EventOutcomeName
              label={label}
              marketName={position.marketName}
              className="text-xs text-foreground/70 line-clamp-1 flex-shrink-0"
            />
            <OptionsPositionBadge
              follow={false}
              options={[
                {
                  type: 'buy',
                  marketAddress: position.marketAddress,
                  label: position.optionDescription ?? position.optionName,
                  value: Number(position.balance),
                  decimal: position.optionDecimal,
                  amount: BigInt(position.balance),
                },
              ]}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                setTradeOpen(true);
              }}
            />
          </div>
        </div>
        {/* Share icon hidden to match base/DID (reference has no share on cards) */}
        {/* <SharePosition uid={uid} position={position} /> */}
        {position.status === PositionStatus.using && (
          <Button
            variant="secondary"
            className="h-auto text-sm font-medium leading-5 py-1 px-4 border border-foreground/4 shadow-none"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              setSellOpen(true);
              setSellParams({
                marketAddress: position.marketAddress,
                optionAddress: position.optionAddress,
              });
            }}
          >
            {t('sell')}
          </Button>
        )}
        {position.status != PositionStatus.using && (
          <Button
            variant="secondary"
            className="h-auto text-sm font-medium leading-5 py-1 px-4 border border-foreground/4 shadow-none"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              setTradeOpen(true);
              setTradeParams({
                marketAddress: position.marketAddress,
                optionAddress: position.optionAddress,
                type: 'buy',
              });
            }}
          >
            {t('claim')}
          </Button>
        )}
      </div>
      <Separator orientation="vertical" className="h-[1px] w-full my-4 bg-foreground/4 " />
      <div className="grid grid-cols-3 grid-rows-2 gap-3">
        <PositionsItemDT label={t('entry-price')} value={position.entryPrice} decimal={position.optionDecimal} baseTokenAddress={position.baseTokenAddress} />
        <PositionsItemDT label={t('market-price')} value={position.marketPirce} decimal={position.optionDecimal} baseTokenAddress={position.baseTokenAddress} />
        <PositionsItemDT label={t('bet')} value={position.betValue} decimal={position.optionDecimal} baseTokenAddress={position.baseTokenAddress} />
        <PositionsItemDT label={t('current')} value={position.currentValue} decimal={position.optionDecimal} baseTokenAddress={position.baseTokenAddress} />
        <PositionsItemDT label={t('pnl')} value={position.pnl} isChange={true} decimal={position.optionDecimal + 4} baseTokenAddress={position.baseTokenAddress} />
        <PositionsItemDT label={t('to-win')} value={position.toWin} decimal={position.optionDecimal} baseTokenAddress={position.baseTokenAddress} />
      </div>
    </div>
  );
};

type PositionsProps = {
  uid: string;
  className?: string;
};
export const Positions = ({ uid, className }: PositionsProps) => {
  const baseTokenAddress = useAppSettingStore(state => state.baseToken.address);
  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = useUserPositions(api, {
    baseTokenAddress,
    page: 1,
    pageSize: 5,
    uid,
  });
  const positionList = useMemo(
    () =>
      data?.pages
        .flatMap(item => item.positions)
        .filter(item => item.status < PositionStatus.loseOption && item.isClaimed != ClaimStatus.yes) ?? [],
    [data]
  );
  const pendingList = usePendingTradesFor(useMemo(() => ({ uid }), [uid]));
  const moreLastIndex = useMemo(() => positionList.length - 2, [positionList.length]);
  const loadMoreItems = async (isInView: boolean, index: number) => {
    if (isInView && index === moreLastIndex && hasNextPage && !isFetchingNextPage) {
      if (IS_DEBUG) console.log('load more positions', uid, moreLastIndex, isInView, index);
      await fetchNextPage();
    }
  };
  return (
    <div className={cn('w-full', className)}>
      <div className="space-y-4">
        {pendingList.map(pt => (
          <PendingPositionRow key={pt.txHash} trade={pt} />
        ))}
        {positionList.map((item, index) => (
          <PositionsItem
            key={`positions-${index}`}
            uid={uid}
            position={item}
            onViewChange={isInView => loadMoreItems(isInView, index)}
          />
        ))}
      </div>
      {(isLoading || isFetchingNextPage) && <BlockLoading />}
      {!isLoading && !hasNextPage && positionList.length === 0 && pendingList.length === 0 && <PositionNothing />}
      {!isLoading && !hasNextPage && positionList.length > 0 && <EndStatus />}
    </div>
  );
};
